// Angular
import {
  CommonModule
} from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  OnInit,
  Output
} from '@angular/core';
import {
  ReactiveFormsModule,
  FormGroup
} from '@angular/forms';

// Material
import { MatIconModule } from '@angular/material/icon';
import { MatCheckboxModule } from '@angular/material/checkbox';

// Third party
import {
  Subscription,
  Observable,
  takeUntil,
  of
} from 'rxjs';

// Local library
import {
  IAccountsACH,
  DepositOnlineCheckService
} from '@amwager/funding/data-access';
import {
  IsEmptyPipe,
  CardNamePipe,
  CardDatePipe,
  CardNumEndPipe,
  DateFormatPipe,
  UnsubscribeObservableService,
  TranslatePipe
} from '@amwager/util';

// Local file
import { AddAccountComponent } from './add-account/add-account.component';

@Component({
  selector: 'feature-mat-online-check-deposit',
  templateUrl: './online-check.component.html',
  styleUrls: ['./online-check.component.scss'],
  imports: [
    CommonModule,
    MatIconModule,
    MatCheckboxModule,
    ReactiveFormsModule,
    IsEmptyPipe,
    CardNamePipe,
    CardDatePipe,
    CardNumEndPipe,
    DateFormatPipe,
    AddAccountComponent,
    TranslatePipe
  ],
  standalone: true,
  providers: [DepositOnlineCheckService],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class OnlineCheckComponent extends UnsubscribeObservableService implements OnInit {

  private subsFundingLimitResponse!: Subscription;
  private subsGetPendingACHResponse!: Subscription;
  private subsDepositAccountResponse!: Subscription;
  private subsGetAccountResponse!: Subscription;
  private subsAddAccountResponse!: Subscription;
  private subsDeleteAccountResponse!: Subscription;
  private tmsId?: string;
  showDepositForm: boolean = true;
  showAddAccountForm: boolean = false;
  @Output() showAddAccountFormFn = new EventEmitter<boolean>();
  onlineCheckForm!: FormGroup;
  bankAccountId?: string | number;
  accounts$!: Observable<IAccountsACH[]>;
  customerFundingLimit: number = 0;
  availableFundingLimit: number = 0;
  pendingList$!: Observable<any[]>;
  amount!: number;
  isAgree: boolean = true;
  isDepositing: boolean = false;

  // convenience getter for easy access to form fields
  get formVal() { return this.onlineCheckForm?.controls }

  constructor(
    private readonly elementRef: ElementRef,
    private readonly cdRef: ChangeDetectorRef,
    private readonly onlineCheckService: DepositOnlineCheckService,
  ) {
    super();

  }

  ngOnInit() {
    this.observerTmsIds();

    // OLD (moved to DepositOnlineCheckService.buildForm):
    // this.initForm();
    this.onlineCheckForm = this.onlineCheckService.buildForm();

    // OLD (getAchAccount + list stream moved to DepositOnlineCheckService.loadAccounts$):
    // this.fundingFacade.getAchAccount();
    // this.subsGetAccountResponse = this.fundingFacade.getAchAccountList$()
    this.subsGetAccountResponse = this.onlineCheckService.loadAccounts$()
      .pipe(takeUntil(this.toUnsubscribe$))
      .subscribe((response) => {
        if (response !== undefined)
          this.accounts$ = of(response);

        this.cdRef.detectChanges();
        this.cdRef.markForCheck();
      });

    // OLD (getFundingLimit + list stream moved to DepositOnlineCheckService.loadFundingLimit$):
    // this.fundingFacade.getFundingLimit();
    // this.subsFundingLimitResponse = this.fundingFacade.getFundingLimitList$()
    this.subsFundingLimitResponse = this.onlineCheckService.loadFundingLimit$()
      .pipe(takeUntil(this.toUnsubscribe$))
      .subscribe((response) => {
        if (response !== undefined) {
          this.customerFundingLimit = response?.CustomerFundingLimit;
          this.availableFundingLimit = response?.AvailableFundingLimit;
        }

        this.cdRef.markForCheck();
      });

    // OLD (getPendingAch + list stream moved to DepositOnlineCheckService.loadPendingAch$):
    // this.fundingFacade.getPendingAch();
    // this.subsGetPendingACHResponse = this.fundingFacade.getPendingAchList$()
    this.subsGetPendingACHResponse = this.onlineCheckService.loadPendingAch$()
      .pipe(takeUntil(this.toUnsubscribe$))
      .subscribe((response) => {
        if (response !== undefined)
          this.pendingList$ = of(response);

        this.cdRef.markForCheck();
      });
  }

  private observerTmsIds(): void {
    // OLD (moved to DepositOnlineCheckService.tmsId$):
    // this.commonFacade?.tmsId$()
    this.onlineCheckService.tmsId$()
      .pipe(takeUntil(this.toUnsubscribe$))
      .subscribe((tmsId: string | undefined) => {
        this.tmsId = tmsId;
      });
  }

  chooseAmount(amount: number): void {
    this.formVal['amount']?.setValue(amount);
    this.amount = amount;
  }

  showAlertMessage(header: string, message: string): void {
    // OLD (moved to DepositOnlineCheckService.showAlert):
    // this.matNotificationService.showAlert(header, message, undefined, undefined, 'Close');
    this.onlineCheckService.showAlert(header, message);
  }

  openAddAccount(): void {
    this.showDepositForm = false;
    this.showAddAccountForm = true;
  }

  confirmRemoveAccount(bankAccountId: string | number): void {
    // OLD (confirmation alert moved to DepositOnlineCheckService.confirmRemoveAccount$):
    // this.matNotificationService.showAlert('Confirmation', '...', undefined, 'Yes', 'Cancel')
    this.onlineCheckService.confirmRemoveAccount$()
    .subscribe((isYes: boolean) => {
      if (isYes) this.removeAccount(bankAccountId);
    });
  }

  removeAccount(bankAccountId: string | number): void {
    // OLD (deleteAchAccount + success stream moved to DepositOnlineCheckService.delete$):
    // this.fundingFacade.deleteAchAccount(bankAccountId);
    // this.subsDeleteAccountResponse = this.fundingFacade.isDeleteAchAccountSuccess$()
    this.subsDeleteAccountResponse = this.onlineCheckService.delete$(bankAccountId)
    .pipe(takeUntil(this.toUnsubscribe$))
    .subscribe(({response, isSuccess}) => {

      if (isSuccess) {
        this.subsDeleteAccountResponse?.unsubscribe();
        return;
      }

      if (isSuccess !== undefined && isSuccess === false) {
        this.showAlertMessage('Error', response?.error?.Message);
        this.subsDeleteAccountResponse?.unsubscribe();
      }

    });
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  chooseAccount($event: any, method: string, account: string, bankAccountId: string | number): void {

    const accountRows = this.elementRef.nativeElement.querySelectorAll(
      '.online-check-per-row'
    );
    accountRows.forEach((accountRow: HTMLElement) => {
      accountRow.classList.remove('active');
    });

    const accountRow = this.elementRef.nativeElement.querySelector(
      '.online-check-content .online-check-list-container #'
      + account
      + '.online-check-per-row'
    );

    if (accountRow.classList.contains('active')) {
      accountRow.classList.remove('active');
      this.bankAccountId = '';

    }
    else {
      accountRow.classList.add('active');
      this.bankAccountId = bankAccountId;
    }

  }

  depositAmount(): void {
    this.amount = this.formVal['amount']?.value;
  }

  deposit(): void {
    if (this.onlineCheckForm.valid && this.amount && this.bankAccountId && this.tmsId) {
      this.isDepositing = true;

      // OLD (dispatch + success stream moved to DepositOnlineCheckService.deposit$):
      // this.fundingFacade.depositOnlineCheck(this.amount, this.bankAccountId, this.tmsId);
      // this.subsDepositAccountResponse = this.fundingFacade.isDepositOnlineCheckSuccess$()
      this.subsDepositAccountResponse = this.onlineCheckService.deposit$(this.amount, this.bankAccountId, this.tmsId)
      .pipe(takeUntil(this.toUnsubscribe$))
      .subscribe(({response, isSuccess}) => {
        this.isDepositing = false;
        if (!isSuccess && response !== undefined) {
          this.showAlertMessage('Deposit Error', response?.error?.Message);
          this.cdRef.markForCheck();
          this.subsDepositAccountResponse?.unsubscribe();
          return;
        }

        if (response?.Error) {
          this.showAlertMessage('Deposit Error', response?.Error);
          this.cdRef.markForCheck();
          this.subsDepositAccountResponse?.unsubscribe();
          return;
        }

        if (isSuccess) {
          this.showAlertMessage(
          'Successful Deposit',
          'ACH Deposit successful, balance will be updated once ACH has been settled by your bank.'
          );
          this.onlineCheckForm.reset();

          this.cdRef.markForCheck();
          this.subsDepositAccountResponse?.unsubscribe();
        }
      });
    }
    else {
      // OLD (moved to DepositOnlineCheckService.markFormGroupTouched):
      // this.fundingService.markFormGroupTouched(this.onlineCheckForm);
      this.onlineCheckService.markFormGroupTouched(this.onlineCheckForm);
    }
  }

  toggleCheckbox(): void {
    this.isAgree = !this.isAgree;
  }

  closeAddAccountForm($event: any): void {
    this.showAddAccountForm = false;
    this.showDepositForm = true;
  }

}
